import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useLocaleChange } from '../../../hooks/useLocaleChange';
import { getErrorMessage } from '../../../api/utils/error-handler';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { wikiNewApi } from '../../../api/wiki-new.service';
import {
  WeaponDetailDto,
  ArmorDetailDto,
  ItemDetailDto,
  NpcDetailDto,
  SkillDetailDto,
} from '../../../types';
import { TEXT_COLORS, BORDER_COLORS } from '../../../utils/colors';

import WeaponDetail from './detail/WeaponDetail';
import ArmorDetail from './detail/ArmorDetail';
import ItemDetail from './detail/ItemDetail';
import NpcDetail from './detail/NpcDetail';
import SkillDetail from './detail/SkillDetail';

interface WikiDetailPanelProps {
  source: string;
  itemId: number;
  detailUrl: string;
  onClose: () => void;
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
}

type DetailData = WeaponDetailDto | ArmorDetailDto | ItemDetailDto | NpcDetailDto | SkillDetailDto[];

const WikiDetailPanel: React.FC<WikiDetailPanelProps> = ({
  source,
  itemId,
  detailUrl,
  onClose,
  raceId,
  skillTypeId,
  classId,
  professionId,
}) => {
  const { t } = useTranslation('wiki');
  const { showError } = useSnackbar();
  // Показываем текущую карточку как ПАРУ {source, data} и НЕ очищаем её, пока грузится следующая —
  // иначе панель мелькает «пусто→спиннер→контент» при каждом переключении. Подмена атомарная (когда новая пришла).
  // Пара source+data также исключает рендер детали чужого типа (SkillDetail на данных предмета → .map упадёт).
  const [displayed, setDisplayed] = useState<{ source: string; data: DetailData } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [languageTrigger, setLanguageTrigger] = useState(0);

  useLocaleChange(useCallback(() => {
    setLanguageTrigger(prev => prev + 1);
  }, []));

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false; // защита от гонки при быстрых кликах — применяем только последнюю загрузку

    const loadDetailData = async () => {
      try {
        setLoading(true);
        setError(false);
        // НЕ сбрасываем displayed — старая карточка остаётся видимой, пока грузится новая

        let result: DetailData;

        switch (source) {
          case 'weapons':
            result = await wikiNewApi.getWeaponDetail(itemId);
            break;
          case 'armors':
          case 'accessories':
          case 'jewelry':
            result = await wikiNewApi.getArmorDetail(itemId);
            break;
          case 'items':
          case 'recipes':
            result = await wikiNewApi.getItemDetail(itemId);
            break;
          case 'npcs':
            result = await wikiNewApi.getNpcDetail(itemId);
            break;
          case 'skills':
            if (raceId && skillTypeId && classId && professionId) {
              result = await wikiNewApi.getSkillDetailByClass(itemId, raceId, skillTypeId, classId, professionId);
            } else {
              result = await wikiNewApi.getSkillDetail(itemId);
            }
            break;
          default:
            throw new Error(t('messages.unsupportedSource', { source }));
        }

        if (!cancelled) setDisplayed({ source, data: result }); // атомарная подмена: source+data вместе
      } catch (err: unknown) {
        if (!cancelled) {
          setError(true);
          showError(getErrorMessage(err, t('messages.loadDetailError')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDetailData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, source, raceId, skillTypeId, classId, professionId, languageTrigger]);

  const getItemName = (): string => {
    if (!displayed) return '';
    const { source: s, data } = displayed;
    if (s === 'skills') {
      const skillsArray = data as SkillDetailDto[];
      return skillsArray.length > 0 ? skillsArray[0].name : '';
    }
    return (data as WeaponDetailDto | ArmorDetailDto | ItemDetailDto | NpcDetailDto).name;
  };

  const renderDetailComponent = () => {
    if (!displayed) return null;
    const { source: s, data } = displayed;

    switch (s) {
      case 'weapons':
        return <WeaponDetail weapon={data as WeaponDetailDto} />;
      case 'armors':
      case 'accessories':
      case 'jewelry':
        return <ArmorDetail armor={data as ArmorDetailDto} />;
      case 'items':
      case 'recipes':
        return <ItemDetail item={data as ItemDetailDto} />;
      case 'npcs':
        return <NpcDetail npc={data as NpcDetailDto} />;
      case 'skills':
        return <SkillDetail skills={data as SkillDetailDto[]} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, color: 'white' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          borderBottom: `1px solid ${BORDER_COLORS.DEFAULT}`,
          flexShrink: 0,
        }}
      >
        <Button
          component={RouterLink}
          to={detailUrl}
          size="small"
          startIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
          sx={{
            color: TEXT_COLORS.MUTED,
            textTransform: 'none',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            px: 1,
            '&:hover': { color: TEXT_COLORS.PRIMARY },
          }}
        >
          {t('drawer.openFullPage')}
        </Button>

        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            fontWeight: 600,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getItemName()}
        </Typography>

        <IconButton onClick={onClose} size="small" sx={{ color: TEXT_COLORS.MUTED }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content — спиннер/ошибку показываем только пока карточки ещё нет (первая загрузка);
          при переключении старая карточка остаётся видимой до атомарной подмены → без мигания. */}
      {!displayed && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={40} />
        </Box>
      ) : !displayed && error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography color="error" variant="body2">{t('drawer.error')}</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 1 }}>
          {renderDetailComponent()}
        </Box>
      )}
    </Box>
  );
};

export default WikiDetailPanel;
